import React, { useEffect, useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import { fetchCampaigns, deleteCampaign } from '../../../services/Http/Api';
import { MdEdit, MdDelete, MdAdd, MdCampaign, MdSearch } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import { toast } from 'react-toastify';
import StandardModal from '../../../shared/layout/modals/StandardModal';

const CampaignList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [campaigns, setCampaigns] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await fetchCampaigns({
                    page: currentPage,
                    perPage,
                    search: searchTerm,
                });
                const { data, total } = response.data;
                setCampaigns(data);
                setTotalRows(total);
            } catch (error) {
                console.error('Error fetching campaigns:', error);
                toast.error(t('Campaigns.loadError', { defaultValue: 'Failed to load campaigns.' }));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentPage, perPage, searchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setResetPaginationToggle(!resetPaginationToggle);
    };

    const handleAddNew = () => {
        navigate('/campaigns/create');
    };

    const handleEditCampaign = (id) => {
        navigate(`/campaigns/edit/${id}`);
    };

    const handleDeleteCampaign = (id) => {
        const selected = campaigns.find((campaign) => campaign.id === id);
        setCampaignToDelete(selected || { id });
    };

    const confirmDeleteCampaign = async () => {
        if (!campaignToDelete?.id) return;
        try {
            await deleteCampaign(campaignToDelete.id);
            toast.success(t('Campaigns.deleteSuccess', { defaultValue: 'Campaign deleted successfully.' }));
            const response = await fetchCampaigns({
                page: currentPage,
                perPage,
                search: searchTerm,
            });
            const { data, total } = response.data;
            setCampaigns(data);
            setTotalRows(total);
            setCampaignToDelete(null);
        } catch (error) {
            console.error('Error deleting campaign:', error);
            toast.error(t('Campaigns.deleteError', { defaultValue: 'Failed to delete campaign.' }));
        }
    };

    const formatDate = (value) => {
        if (!value) return 'N/A';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return parsed.toLocaleDateString();
    };

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '52px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb !important',
                    transition: 'background-color 0.2s',
                },
            },
        },
    };

    const columns = useMemo(
        () => [
            { name: t('Campaigns.name'), selector: row => row.name || 'N/A', sortable: true },
            { name: t('Campaigns.description'), selector: row => row.description || 'N/A', sortable: true },
            { name: t('Campaigns.startDate'), selector: row => formatDate(row.start_date), sortable: true },
            { name: t('Campaigns.endDate'), selector: row => formatDate(row.end_date), sortable: true },
            { name: t('Campaigns.goalDescription'), selector: row => row.goal_description || 'N/A', sortable: true },
            { name: t('Campaigns.budget'), selector: row => row.budget || 'N/A', sortable: true },
            {
                name: t('Campaigns.actions'),
                cell: row => (
                    <ActionButtons>
                        <MdEdit className="edit-icon" onClick={() => handleEditCampaign(row.id)} />
                        <MdDelete className="delete-icon" onClick={() => handleDeleteCampaign(row.id)} />
                    </ActionButtons>
                ),
                ignoreRowClick: true,
            },
        ],
        [t]
    );

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdCampaign size={28} />}>{t('Campaigns.title') || 'Campaigns'}</PageTitle>
            </PageHeader>
            <PageContent>
                <HeaderControls>
                    <SearchContainer>
                        <MdSearch size={20} />
                        <input
                            type="text"
                            placeholder={t('Campaigns.search')}
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </SearchContainer>
                    <MainButton size="small" onClick={handleAddNew}>
                        <MdAdd size={20} />
                        {t('Campaigns.addNew')}
                    </MainButton>
                </HeaderControls>

                <StyledCard noPadding>
                    <DataTable
                        columns={columns}
                        data={campaigns}
                        pagination={totalRows > perPage}
                        paginationServer
                        persistTableHead
                        highlightOnHover
                        resetPaginationToggle={resetPaginationToggle}
                        paginationTotalRows={totalRows}
                        onChangePage={(page) => setCurrentPage(page)}
                        onChangeRowsPerPage={(newPerPage, newPage) => {
                            setPerPage(newPerPage);
                            setCurrentPage(newPage);
                        }}
                        progressPending={loading}
                        customStyles={customStyles}
                        noDataComponent={
                            <NoDataState>
                                <strong>{t('Campaigns.emptyTitle', { defaultValue: 'No campaigns yet' })}</strong>
                                <span>{t('Campaigns.emptyDescription', { defaultValue: 'Create your first campaign to start tracking performance.' })}</span>
                            </NoDataState>
                        }
                        noHeader
                    />
                </StyledCard>
            </PageContent>
            <StandardModal
                isOpen={Boolean(campaignToDelete)}
                onClose={() => setCampaignToDelete(null)}
                title={t('Campaigns.deleteTitle', { defaultValue: 'Delete campaign' })}
                footer={(
                    <>
                        <MainButton variant="secondary" onClick={() => setCampaignToDelete(null)}>
                            {t('common.cancel', { defaultValue: 'Cancel' })}
                        </MainButton>
                        <MainButton variant="danger" onClick={confirmDeleteCampaign}>
                            {t('common.delete', { defaultValue: 'Delete' })}
                        </MainButton>
                    </>
                )}
            >
                <p style={{ margin: 0 }}>
                    {t('Campaigns.deleteConfirm', { defaultValue: 'Are you sure you want to delete this campaign?' })}
                    {campaignToDelete?.name ? ` (${campaignToDelete.name})` : ''}
                </p>
            </StandardModal>
        </PageContainer>
    );
};

export default CampaignList;

// Styled Components
const HeaderControls = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    gap: 1rem;

    @media (max-width: 640px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const SearchContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    background: #f3f4f6;
    border-radius: 12px;
    padding: 0 1rem;
    flex: 1;
    max-width: 400px;
    border: 1.5px solid transparent;
    transition: all 0.2s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

    &:focus-within {
        background: #fff;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 4px rgba(130, 22, 146, 0.1);
    }

    input {
        background: transparent;
        border: none;
        padding: 0.75rem 0.75rem;
        width: 100%;
        outline: none;
        font-size: 0.875rem;
        color: #111827;
        
        &::placeholder {
            color: #9ca3af;
        }
    }

    svg {
        color: #6b7280;
        transition: color 0.2s;
    }

    &:focus-within svg {
        color: var(--color-primary);
    }

    @media (max-width: 640px) {
        max-width: none;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 0.5rem;

    .edit-icon, .delete-icon {
        width: 32px;
        height: 32px;
        padding: 0.375rem;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        color: #64748b;

        &:hover {
            background: #f1f5f9;
        }
    }

    .edit-icon:hover {
        color: var(--color-primary);
        background: rgba(130, 22, 146, 0.1);
    }

    .delete-icon:hover {
        color: #ef4444;
        background: rgba(239, 68, 68, 0.1);
    }
`;

const NoDataState = styled.div`
    padding: 2rem 1rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    color: #64748b;
`;
